import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  TextField,
  Button,
  Box,
} from "@material-ui/core";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import LoadingButton from "@material-ui/lab/LoadingButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "src/components/snackbar";
import { fechaActual } from "src/utils/formatTime";

const validationSchema = Yup.object().shape({
  cantidad: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("Debe ser un número")
    .integer("Debe ser un número entero")
    .min(0, "No puede ser negativo")
    .required("Este campo es obligatorio"),
});

export default function CantidadRemuneracionesDialog({
  open,
  onClose,
  tarea,
  onGuardar,
}) {
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: { cantidad: "" },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const fechaTermino = fechaActual().format("YYYY/MM/DD");
        await onGuardar({
          ...tarea,
          cantidadFactura: values.cantidad,
          fechaTermino,
        });
        enqueueSnackbar("Liquidaciones guardadas correctamente", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
        onClose();
      } catch (error) {
        enqueueSnackbar(
          error?.data?.message || "Error al guardar las liquidaciones",
          {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
          },
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (open) {
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} fullWidth maxWidth="xs">
      <DialogTitle disableTypography>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <PeopleAltIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Registrar Liquidaciones
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Tarea marcada como terminada
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Stack spacing={3}>
            <Box
              sx={{
                bgcolor: "grey.100",
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography variant="body2">
                <strong>Tarea:</strong> {tarea?.nombre}
              </Typography>
              <Typography variant="body2" mt={0.5}>
                <strong>Cliente:</strong> {tarea?.nombreCliente}
              </Typography>
            </Box>

            <TextField
              fullWidth
              type="number"
              name="cantidad"
              label="Cantidad de liquidaciones"
              value={formik.values.cantidad}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onWheel={(e) => e.target.blur()}
              error={formik.touched.cantidad && Boolean(formik.errors.cantidad)}
              helperText={
                formik.touched.cantidad && formik.errors.cantidad
                  ? formik.errors.cantidad
                  : "Ingresa 0 si no hubo liquidaciones este período"
              }
              inputProps={{ min: 0, step: 1 }}
              disabled={formik.isSubmitting}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={formik.isSubmitting}>
            Cancelar
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={formik.isSubmitting}
          >
            Guardar
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
